/**
 * Procedural Audio for LUMEN clearings
 * Pure Web Audio synthesis — no samples.
 * 
 * Layers:
 * - Base: filtered brown-ish noise (wind / distant water)
 * - Events: sparse, pleasant "bioacoustics" (chirps, ticks, soft clicks)
 * - Real-time coupling: intensity + mouse/tilt modulate filter cutoff and event density
 */

export class ClearingAudio {
  private ctx: AudioContext | null = null;
  private running = false;

  // Nodes
  private masterGain!: GainNode;
  private noise!: AudioBufferSourceNode;
  private noiseFilter!: BiquadFilterNode;
  private noiseGain!: GainNode;

  private eventGain!: GainNode;
  private panner!: StereoPannerNode;

  private intensity = 0.85;
  private volume = 0.65;
  private muted = false;

  private eventTimer: number | null = null;

  async start() {
    if (this.running) return;
    this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();

    // Brown-ish noise base via ScriptProcessor (classic technique)
    const bufferSize = 4096;
    const noiseBuffer = this.ctx.createBuffer(1, this.ctx.sampleRate * 3, this.ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    let lastOut = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      data[i] = (lastOut + (0.02 * white)) / 1.02; // brown
      lastOut = data[i];
    }

    this.noise = this.ctx.createBufferSource();
    this.noise.buffer = noiseBuffer;
    this.noise.loop = true;

    this.noiseFilter = this.ctx.createBiquadFilter();
    this.noiseFilter.type = 'lowpass';
    this.noiseFilter.frequency.value = 420;

    this.noiseGain = this.ctx.createGain();
    this.noiseGain.gain.value = 0.035;

    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.volume;

    this.eventGain = this.ctx.createGain();
    this.eventGain.gain.value = 0.7;

    this.panner = this.ctx.createStereoPanner();

    // Graph: noise -> filter -> gain -> master
    // events -> eventGain -> panner -> master
    const merger = this.ctx.createGain();

    this.noise.connect(this.noiseFilter);
    this.noiseFilter.connect(this.noiseGain);
    this.noiseGain.connect(merger);

    this.eventGain.connect(this.panner);
    this.panner.connect(merger);

    merger.connect(this.masterGain);
    this.masterGain.connect(this.ctx.destination);

    this.noise.start();

    this.running = true;
    this.scheduleEvents();
  }

  stop() {
    if (!this.running) return;
    this.running = false;
    if (this.eventTimer) window.clearTimeout(this.eventTimer);
    try { this.noise.stop(); } catch {}
    if (this.ctx) this.ctx.close();
    this.ctx = null;
  }

  setIntensity(v: number) {
    this.intensity = Math.max(0.2, Math.min(1.6, v));
    if (this.noiseFilter) {
      // brighter when higher intensity (more "life")
      const f = 280 + this.intensity * 380;
      this.noiseFilter.frequency.value = f;
    }
    if (this.noiseGain) {
      this.noiseGain.gain.value = 0.028 + this.intensity * 0.018;
    }
  }

  setVolume(v: number) {
    this.volume = v;
    if (this.masterGain) this.masterGain.gain.value = this.muted ? 0 : v;
  }

  toggleMute() {
    this.muted = !this.muted;
    if (this.masterGain) this.masterGain.gain.value = this.muted ? 0 : this.volume;
    return this.muted;
  }

  // Called from scene loop with normalized mouse/tilt for spatial interest
  updateSpatial(mx: number, tiltX: number) {
    if (!this.panner) return;
    // Gentle stereo movement
    const pan = (mx - 0.5) * 0.6 + tiltX * 0.25;
    this.panner.pan.value = Math.max(-0.85, Math.min(0.85, pan));
  }

  private scheduleEvents() {
    if (!this.running || !this.ctx) return;

    const baseRate = 420 + (1 - this.intensity) * 180; // ms between candidate events
    const delay = baseRate * (0.6 + Math.random() * 1.1);

    this.eventTimer = window.setTimeout(() => {
      if (!this.running || !this.ctx) return;
      this.playBioEvent();
      this.scheduleEvents();
    }, delay);
  }

  private playBioEvent() {
    if (!this.ctx) return;

    const now = this.ctx.currentTime;
    const type = Math.random();

    // Common "insect / bird-like" transient
    if (type < 0.65) {
      // Soft high chirp
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 1650 + Math.random() * 620;

      const gain = this.ctx.createGain();
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = osc.frequency.value;
      filter.Q.value = 6.5;

      const p = this.ctx.createStereoPanner();
      p.pan.value = (Math.random() - 0.5) * 0.9;

      const dur = 0.038 + Math.random() * 0.045;

      gain.gain.value = 0.0001;
      gain.gain.linearRampToValueAtTime(0.028 + this.intensity * 0.012, now + 0.012);
      gain.gain.linearRampToValueAtTime(0.0001, now + dur + 0.09);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(p);
      p.connect(this.eventGain);

      osc.start(now);
      osc.stop(now + dur + 0.12);
    } else {
      // Subtle water-tick or soft click (brown noise burst shaped)
      const src = this.ctx.createBufferSource();
      const buf = this.ctx.createBuffer(1, Math.floor(this.ctx.sampleRate * 0.09), this.ctx.sampleRate);
      const d = buf.getChannelData(0);
      let last = 0;
      for (let i = 0; i < d.length; i++) {
        const w = Math.random() * 2 - 1;
        d[i] = (last + 0.06 * w) / 1.06;
        last = d[i];
      }
      src.buffer = buf;

      const g = this.ctx.createGain();
      const f = this.ctx.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = 1250 + Math.random() * 400;

      g.gain.value = 0.012 + this.intensity * 0.008;
      g.gain.linearRampToValueAtTime(0.0004, now + 0.11);

      const p = this.ctx.createStereoPanner();
      p.pan.value = (Math.random() - 0.5) * 1.1;

      src.connect(f);
      f.connect(g);
      g.connect(p);
      p.connect(this.eventGain);

      src.start(now);
    }
  }
}
